import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, HERITAGE_ASSETS } from '@/lib/api';
import { Panel, SectionDivider } from '@/components/Panel';
import { HeritageSeal } from '@/components/BrandMark';
import { ArrowRight } from 'lucide-react';

function ProductCard({ product }) {
  const soldOut = !product.in_stock;
  const colours = Array.from(new Set((product.variants || []).map((v) => v.colour).filter(Boolean)));
  return (
    <Link
      to={`/store/${product.slug}`}
      className="group block"
      data-testid={`store-card-${product.slug}`}
    >
      <Panel className="overflow-hidden p-0 transition-transform duration-200 group-hover:-translate-y-0.5">
        <div className="relative aspect-[4/5] overflow-hidden" style={{ background: '#FBF8F1' }}>
          <img
            src={product.image_url || HERITAGE_ASSETS.editorial}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            style={{ opacity: soldOut ? 0.55 : 1 }}
          />
          {soldOut && (
            <span className="absolute top-3 left-3 h-chip text-[10px]" style={{ background: 'rgba(75,24,56,0.85)', color: '#F6F0E4', borderColor: 'transparent' }}>
              Sold out
            </span>
          )}
          {!soldOut && product.total_stock > 0 && product.total_stock <= 10 && (
            <span className="absolute top-3 left-3 h-chip text-[10px]" style={{ background: 'rgba(154,75,126,0.9)', color: '#F6F0E4', borderColor: 'transparent' }}>
              Only {product.total_stock} left
            </span>
          )}
        </div>
        <div className="px-5 pt-4 pb-5">
          <div className="h-eyebrow text-[10px]">{product.subtitle || product.collection_name}</div>
          <div className="h-serif text-xl leading-tight mt-1" style={{ color: 'var(--heritage-plum)' }}>{product.name}</div>
          <div className="mt-2 text-sm opacity-80" style={{ color: 'var(--heritage-plum)' }}>${product.price} {product.currency}</div>
          {colours.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3">
              {colours.slice(0, 4).map((c) => {
                const v = (product.variants || []).find((x) => x.colour === c);
                return (
                  <span
                    key={c}
                    title={c}
                    className="w-4 h-4 rounded-full border"
                    style={{ background: v?.colour_hex || '#EAE0D2', borderColor: 'rgba(75,24,56,0.3)' }}
                  />
                );
              })}
              <span className="text-[10px] uppercase tracking-widest opacity-70 ml-1" style={{ color: 'var(--heritage-plum)' }}>
                {colours.length} colour{colours.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
          <div className="mt-4 inline-flex items-center gap-1 text-xs opacity-80 group-hover:opacity-100" style={{ color: 'var(--heritage-plum)' }}>
            Shop this piece <ArrowRight size={12} />
          </div>
        </div>
      </Panel>
    </Link>
  );
}

export default function StorePage() {
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState({
    hero_headline: 'Heritage Signature Collection',
    hero_sub: 'Small batch. Numbered. Made to last. Every Heritage piece is embroidered by hand from our workshop in Aotearoa.',
    hero_image_url: '',
    store_intro: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [prods, cnt] = await Promise.all([
          api.get('/shop/products'),
          api.get('/shop/content').catch(() => ({ data: null })),
        ]);
        setProducts(prods.data.products || []);
        if (cnt?.data) setContent((c) => ({ ...c, ...cnt.data }));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const apparel = products.filter((p) => p.product_type === 'apparel');
  const socks = products.filter((p) => p.product_type === 'socks');

  return (
    <div style={{ background: 'var(--heritage-cream)' }}>
      {/* Hero */}
      <div className="relative w-full overflow-hidden" style={{ background: '#4B1838' }}>
        <img
          src={content.hero_image_url || HERITAGE_ASSETS.heroSubBanner}
          alt="Heritage store"
          className="w-full max-h-[460px] object-cover"
          style={{ objectPosition: 'center top' }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-10 text-center">
        <HeritageSeal size={72} />
        <div className="h-eyebrow mt-4">The Store</div>
        <h1 className="h-display text-5xl mt-2" style={{ color: 'var(--heritage-plum)' }}>{content.hero_headline}</h1>
        <p className="opacity-85 max-w-2xl mx-auto mt-3" style={{ color: 'var(--heritage-plum)' }}>
          {content.hero_sub}
        </p>
        {content.store_intro && (
          <p className="opacity-75 max-w-2xl mx-auto mt-4 text-sm italic" style={{ color: 'var(--heritage-plum)' }}>
            {content.store_intro}
          </p>
        )}
      </div>

      {loading ? (
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="store-skeleton">
            {[0, 1, 2, 3, 4, 5].map((k) => (
              <div key={k} className="aspect-[4/5] rounded-2xl animate-pulse" style={{ background: 'rgba(75,24,56,0.08)' }} />
            ))}
          </div>
        </div>
      ) : (
        <>
          <SectionDivider label="Apparel" />
          <div className="max-w-6xl mx-auto px-6 pb-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="store-apparel-grid">
            {apparel.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>

          {socks.length > 0 && (
            <>
              <SectionDivider label="Socks" />
              <div className="max-w-6xl mx-auto px-6 pb-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="store-socks-grid">
                {socks.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            </>
          )}

          {products.length === 0 && (
            <div className="max-w-3xl mx-auto px-6 pb-20">
              <Panel className="p-10 text-center">
                <div className="h-serif text-2xl" style={{ color: 'var(--heritage-plum)' }}>Restocking soon</div>
                <div className="text-sm opacity-80 mt-2" style={{ color: 'var(--heritage-plum)' }}>The next Heritage release is on the way.</div>
              </Panel>
            </div>
          )}
        </>
      )}
    </div>
  );
}
